import { canStepForLevel } from './walkability';
import type { CanStep } from './mobile';
import type { World } from './world';

/**
 * The movement system: a pure function over the {@link World} that advances
 * every grid-stepped entity carrying a {@link Mobile} by one fixed step.
 * Marked dungeon enemies are advanced by the dungeon's continuous steering
 * layer, so this system deliberately leaves their precise positions untouched.
 *
 * It wraps the existing, already-faithful `Mobile` state machine (walk/run, the
 * turn-in-place beat, idle) unchanged - the system only feeds it the fixed
 * `stepMs` and a walkability predicate, then mirrors the mobile's authoritative
 * resting tile and facing back into the entity's position component so the rest
 * of the world (and the renderer) can read movement as plain entity state.
 *
 * Walkability is resolved **per level** through the edge-based
 * {@link canStepForLevel} (true terrain height, PRD #536 Slice A): a ground
 * entity steps through {@link doorAwareWalkable} - the static mask AND the
 * live door state (Slice 4, ADR-0014) - composed with the map's climb rule
 * (`|landZ(to) - landZ(from)| <= CLIMB`), while one standing on a roof plate
 * steps through the roof-cell rule (plates are flat; no climb), so the ground
 * floor's interior walls and doors never leak upward. Predicates are built
 * lazily once per distinct level per tick; each mobile sees a plain
 * `IsWalkable` closed over its own live resting tile (the `from` endpoint), so
 * `Mobile` stays 2D and oblivious to doors, roofs, levels, and heights alike.
 *
 * **Ground z is derived, never authored** (PRD #536): while on the ground
 * level, the system mirrors `position.z = landZ(x, y)` on every committed step
 * - the same mirror that syncs x/y from the mobile. An entity counts as
 * ground-level when its z is 0 (the flat default, incl. absent) or equals the
 * terrain height under it; anything else is a roof-plate level and its z is
 * only ever changed by a teleporter, exactly as before. On a flat map the
 * mirror never writes (landZ is 0 everywhere and z is already 0/absent), so
 * wire bytes and snapshots are untouched.
 *
 * After mirroring, a **teleporter** check runs: when a mobile *completed a
 * step* this tick onto a teleporter entity's cell (same tile, same level), it
 * is moved to the teleporter's destination - tile and level - via
 * {@link Mobile.teleport}, which snaps the interpolation state while keeping
 * facing and any held intent. Only a completed step fires it (arriving *by*
 * teleport does not), so a mirrored pair can point at each other without
 * ping-ponging; stepping off and back on is what returns you.
 *
 * It reads no wall clock and no unseeded randomness, so the sim stays
 * deterministic and replayable.
 */
export function movementSystem(world: World, stepMs: number): void {
  // One predicate per distinct level this tick, built on first use. Level 0 is
  // the common case and every predicate snapshot (door set, roof index) is
  // world-state that must be consistent across all mobiles in the tick.
  const byLevel = new Map<number, CanStep>();
  const canStepAt = (z: number) => {
    let fn = byLevel.get(z);
    if (!fn) {
      fn = canStepForLevel(world, z);
      byLevel.set(z, fn);
    }
    return fn;
  };
  const teleporters = teleportersByCell(world);

  for (const entity of world.all()) {
    const mobile = entity.mobile;
    if (!mobile) continue;
    // Dungeon enemies own an exact continuous position in dungeonCombat.
    // Their Mobile is only the containing-cell and facing anchor, so advancing
    // or mirroring it here would quantize that authoritative coordinate.
    if (entity.dungeonEnemy === true) continue;
    // Ground vs roof-plate level via the shared GameMap.levelAt normalization
    // (Slice B): ground is z 0 (the flat default, incl. a just-spawned entity
    // awaiting its first mirror) or z equal to the terrain height underfoot.
    // On a flat map landZ is 0 everywhere, so this is exactly the old `!z` test.
    const level = world.map.levelAt(mobile.tileX, mobile.tileY, entity.position?.z);
    const onGround = level === 0;
    const canStep = canStepAt(level);
    const before = { x: mobile.tileX, y: mobile.tileY };
    // Close the edge predicate over the mobile's live resting tile: `decide`
    // only ever probes neighbours of the tile it currently rests on, so the
    // closure stays correct even when one big dt chains several steps.
    mobile.update(stepMs, (x, y) => canStep({ x: mobile.tileX, y: mobile.tileY }, { x, y }));
    if (!entity.position) continue;
    entity.position.x = mobile.tileX;
    entity.position.y = mobile.tileY;
    entity.position.facing = mobile.facing;
    if (onGround) {
      // Mirror the derived ground z on committed steps. Guarded so a flat
      // map's absent z stays absent (never materialise `z: 0` where it wasn't
      // written before - snapshots and wire bytes must be untouched).
      const groundZ = world.map.landZ(mobile.tileX, mobile.tileY);
      if ((entity.position.z ?? 0) !== groundZ) entity.position.z = groundZ;
    }

    // Teleport on step completion: the resting tile moved this tick and now
    // sits on a teleporter of the entity's own LEVEL (a ground walker carrying a
    // mirrored terrain z still matches a ground teleporter - Slice B). A world
    // with no teleporters short-circuits before building the cell key, so a
    // dense Arena run does not allocate one key string per stepping enemy per
    // tick to look up an index that is empty by construction (PRD #825).
    if (teleporters.size === 0) continue;
    if (mobile.tileX === before.x && mobile.tileY === before.y) continue;
    const dest = teleporters.get(teleporterCellKey(mobile.tileX, mobile.tileY, level));
    if (!dest) continue;
    mobile.teleport(dest.x, dest.y);
    entity.position.x = dest.x;
    entity.position.y = dest.y;
    // A ground destination (authored `to.z = 0`) lands ON the terrain
    // (PRD #536 Slice B); a roof-plate destination keeps its plate z.
    entity.position.z = world.map.groundedZ(dest.x, dest.y, dest.z);
  }
}

/**
 * The teleporter index's cell key: tile + **level** (never a raw `position.z`),
 * so a ground teleporter never fires for a rooftop walker crossing the same
 * (x, y) - and, on heighted terrain, a ground teleporter authored at `z: 0` (or
 * at its tile's own landZ) still matches a walker arriving with the mirrored
 * terrain z (PRD #536 Slice B). Exported with {@link teleportersByCell} so the
 * server's per-step walk handler shares the exact same lookup the tick's
 * movement system applies. Callers pass the LEVEL - normalize a raw z through
 * `GameMap.levelAt` first.
 */
export function teleporterCellKey(x: number, y: number, z: number): string {
  return `${x},${y},${z}`;
}

/** The shared answer for a world holding no teleporters at all; never mutated. */
const NO_TELEPORTERS: ReadonlyMap<string, { x: number; y: number; z: number }> = new Map();

/**
 * Index the world's teleporters by their source cell (tile + level, normalized
 * through `GameMap.levelAt` so heighted-terrain authoring at either `z: 0` or
 * the tile's landZ resolves to the one ground level). Exported for the
 * server's per-step walk handler, so a predicted step landing on a teleporter
 * is moved by the identical rule the intent-driven movement system fires on
 * step completion.
 *
 * Driven by the world's teleporter presence index (PRD #825), so this is
 * O(teleporters), not O(entities): a world with none - every Arena run world,
 * every dungeon floor - gets the shared empty map back with no scan and no
 * allocation, instead of walking its whole entity set once per tick.
 */
export function teleportersByCell(
  world: World,
): ReadonlyMap<string, { x: number; y: number; z: number }> {
  const teleporters = world.withComponent('teleporter');
  if (teleporters.size === 0) return NO_TELEPORTERS;
  const cells = new Map<string, { x: number; y: number; z: number }>();
  for (const entity of teleporters) {
    const teleporter = entity.teleporter;
    const pos = entity.position;
    if (!teleporter || !pos) continue;
    cells.set(teleporterCellKey(pos.x, pos.y, world.map.levelAt(pos.x, pos.y, pos.z)), teleporter.to);
  }
  return cells;
}
